import React from "react";
import { ContactForm } from "../../../types/ContactForm";
import IconButton from "../../../components/IconButton";
import { faCopy, faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import cx from "classnames";
import styles from '../ContactFormOverview.module.scss';

type FormListItem = {
    id: string;
    name: string;
    active: boolean;
    created_at?: string | null;
    config?: ContactForm['config'];
};

type ContactFormListProps = {
    forms: FormListItem[];
    onToggleVisibility: (formId: string) => void;
    onDelete: (formId: string, formName: string) => void;
    onDuplicate: (formId: string) => void;
    onEditName: (formId: string, formName: string) => void;
};

const Index: React.FC<ContactFormListProps> = ({
    forms,
    onDelete,
    onToggleVisibility,
    onDuplicate,
    onEditName
}) => {
    const { useRoute: { changeRouteWithParams } } = useGlobalContext();

    const handleEdit = (formId: string) => {
        changeRouteWithParams('ContactForm', { formId });
    };

    const handleEditName = (formId: string, formName: string) => {
        onEditName(formId, formName);
    };

    const formatInjectionInfo = (form: FormListItem): string => {
        const config = form.config;
        
        if (!config || !config.autoInjectEnabled) {
            return 'Template-Injection: Nicht aktiviert';
        }

        const parts: string[] = [];
        
        if (config.injectionSelector) {
            parts.push(`Selector: ${config.injectionSelector}`);
        }
        
        if (config.injectionPages) {
            const pages: string[] = config.injectionPages.split(',').map((p: string) => p.trim()).filter((p: string) => p.length > 0);
            if (pages.length > 0) {
                const displayPages: string = pages.length > 2 
                    ? `${pages.slice(0, 2).join(', ')}... (+${pages.length - 2})`
                    : pages.join(', ');
                parts.push(`Seiten: ${displayPages}`);
            }
        }
        
        return parts.length > 0 ? `Template-Injection: ${parts.join(' • ')}` : 'Template-Injection: Aktiviert';
    };

    return (
        <div className={cx(styles['list-container'])}>
            {forms.map((form) => (
                <div key={form.id} className={cx(styles['list-item'], 'zr-d-flex', 'zr-justify-between', 'zr-items-center')}>
                    <div className={cx(styles['form-info'])}>
                        <div 
                            className={cx(styles['list-title'])}
                            onClick={() => handleEdit(form.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            {form.name}
                        </div>
                        <div className={'zr-d-flex zr-items-center zr-gap-3'}>
                            <div className={cx(styles['injection-info'])}>
                                {formatInjectionInfo(form)}
                            </div>
                            <button
                                type="button"
                                className={cx(styles['status-badge'], styles['status-badge-clickable'], form.active ? styles['active'] : styles['inactive'])}
                                onClick={() => onToggleVisibility(form.id)}
                            >
                                {form.active ? 'Aktiv' : 'Inaktiv'}
                            </button>
                        </div>
                    </div>
                    <div className={cx(styles['list-actions'], 'zr-d-flex', 'zr-items-center')} style={{gap: '1rem'}}>
                        <div className={cx('zr-d-flex', 'zr-items-center')}>
                            <IconButton
                                icon={faEdit}
                                color="#2f2f2f"
                                title="Umbenennen"
                                onClick={() => handleEditName(form.id, form.name)}
                            />
                            <IconButton
                                icon={faCopy}
                                color="#2f2f2f"
                                title="Duplizieren"
                                onClick={() => onDuplicate(form.id)}
                            />
                            <IconButton
                                icon={faTrashAlt}
                                color="#2f2f2f"
                                title="Löschen"
                                onClick={() => onDelete(form.id, form.name)}
                            />
                        </div>
                        <span 
                            onClick={() => handleEdit(form.id)} 
                            className={cx(styles['list-demo'])}
                        >
                            Bearbeiten
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Index;